export const langs = {
  ja: 'ja-JP',
  en: 'en-US',
  cn: 'zh-CN',
  tw: 'zh-TW',
  zh: 'zh-CN', // 短縮コードの場合は簡体字を適用
  ko: 'ko-KR',
} as const;

const defaultLanguage = langs.ja;

/**
 * 言語コードを完全な形式に変換する関数
 * - `langs` にそのまま存在する場合は、そのまま返す
 * - 短縮形 (`ja` など) の場合、`langs` に当てはまるものを探す
 * - 短縮形も見つからなければ `defaultLanguage` を返す
 *
 * @param code - 言語コード（短い形式または完全な形式）
 * @returns 変換された完全な言語コード
 */
export function convertLanguageCode(code: string | undefined): string {
  if (!code) {
    return defaultLanguage;
  }

  // 完全な形式が `langs` にある場合はそのまま返す
  if (Object.values(langs).includes(code as any)) {
    return code;
  }

  // 短い形式（`ja` など）が `langs` にある場合は変換
  const shortCode = code.split('-')[0]; // 'ja-JP' -> 'ja'
  if (langs[shortCode as keyof typeof langs]) {
    return langs[shortCode as keyof typeof langs];
  }

  // フォールバック
  return defaultLanguage;
}

/**
 * 使用する言語を1つ決定する関数
 * @param detectedLanguage - string | string[] | undefined
 * @returns 最終的に使用する言語コード
 */
export function determineLanguage(
  detectedLanguage: string | string[] | undefined,
): string {
  // 入力がundefinedの場合
  if (!detectedLanguage) {
    return defaultLanguage;
  }

  // 入力が配列の場合
  if (Array.isArray(detectedLanguage)) {
    for (const lang of detectedLanguage) {
      const convertedLang = convertLanguageCode(lang);
      if (convertedLang !== defaultLanguage) {
        return convertedLang; // 最初に有効な言語を返す
      }
    }
    return defaultLanguage; // 配列内に有効な言語がない場合
  }

  // 入力がstringの場合
  return convertLanguageCode(detectedLanguage);
}
